/**
 * Alarm Sound Manager — Super Admin
 * Path: /admin/alarm-sounds
 * Upload audio, categorise (medicine / vaccine / feed / …), preview, mark
 * as default and toggle active. Customer app then plays these on reminder.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError, API_BASE } from "../../lib/api";
import {
  UploadSimple, Play, Pause, Trash, PencilSimple, X,
  SpeakerHigh, Star, ArrowsClockwise, Plus, MusicNote,
} from "@phosphor-icons/react";

const CATEGORIES = [
  { key: "medicine", label: "Medicine · Pills", color: "#DC2626" },
  { key: "vaccine",  label: "Vaccine",          color: "#B91C1C" },
  { key: "feed",     label: "Baby Feed",         color: "#DB2777" },
  { key: "kick",     label: "Kick Count",       color: "#E11D48" },
  { key: "pms",      label: "PMS / Cycle",       color: "#9F1239" },
  { key: "pet",      label: "Pet Care",          color: "#15803D" },
  { key: "generic",  label: "Generic Alarm",    color: "#5C5A55" },
];
const LANGS = [
  { v: "", l: "—" }, { v: "en", l: "English" }, { v: "ml", l: "Malayalam" },
  { v: "hi", l: "Hindi" }, { v: "ta", l: "Tamil" }, { v: "te", l: "Telugu" },
  { v: "kn", l: "Kannada" }, { v: "bn", l: "Bengali" },
];
const GENDERS = [
  { v: "", l: "—" }, { v: "female", l: "Female" }, { v: "male", l: "Male" }, { v: "neutral", l: "Neutral" },
];

export default function AlarmSoundManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("medicine");
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const [editing, setEditing] = useState(null); // row being edited
  const [showUpload, setShowUpload] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/crm/alarm-sounds");
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingId(null);
  };
  const play = (row) => {
    stopAudio();
    // Absolute URL so `Audio` can fetch it regardless of the frontend origin
    const url = row.url.startsWith("/") ? `${API_BASE.replace(/\/api$/, "")}${row.url}` : row.url;
    const audio = new Audio(url);
    audio.onended = () => setPlayingId(null);
    audio.onerror = () => { setPlayingId(null); toast.error("Could not play this file"); };
    audio.play();
    audioRef.current = audio;
    setPlayingId(row.id);
  };
  useEffect(() => () => stopAudio(), []);

  const setDefault = async (row) => {
    try { await api.post(`/crm/alarm-sounds/${row.id}/set-default`); toast.success("Set as default"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try { await api.delete(`/crm/alarm-sounds/${row.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleActive = async (row) => {
    try { await api.put(`/crm/alarm-sounds/${row.id}`, { active: !row.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const rowsForTab = useMemo(() => rows.filter(r => r.category === tab), [rows, tab]);
  const counts = useMemo(() => {
    const m = {};
    CATEGORIES.forEach(c => { m[c.key] = 0; });
    rows.forEach(r => { m[r.category] = (m[r.category] || 0) + 1; });
    return m;
  }, [rows]);

  return (
    <AppShell
      title="Alarm Sounds"
      subtitle="Upload & assign audio for customer reminder alarms"
      actions={
        <div className="flex gap-2">
          <button onClick={load} data-testid="alarms-refresh"
            className="inline-flex items-center gap-2 h-10 px-3 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]">
            <ArrowsClockwise size={12} weight="bold"/> Refresh
          </button>
          <button onClick={() => setShowUpload(true)} data-testid="alarms-upload"
            className="inline-flex items-center gap-2 h-10 px-4 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-semibold">
            <UploadSimple size={12} weight="bold"/> Upload audio
          </button>
        </div>
      }
    >
      <div className="space-y-5" data-testid="alarm-sounds-mgr">
        {/* Tabs */}
        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
          <div className="flex">
            {CATEGORIES.map(c => (
              <button key={c.key}
                onClick={() => setTab(c.key)}
                data-testid={`alarms-tab-${c.key}`}
                className={`h-11 px-4 text-xs font-semibold whitespace-nowrap border-b-2 inline-flex items-center gap-2 ${
                  tab === c.key
                    ? "border-[#DC2626] text-[#DC2626] bg-white"
                    : "border-transparent text-[#5C5A55] hover:text-[#111827]"
                }`}>
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: c.color }}/>
                {c.label}
                <span className="text-[10px] font-mono text-[#8A8780]">{counts[c.key] || 0}</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-14 text-center text-sm text-[#8A8780]">Loading…</div>
        ) : rowsForTab.length === 0 ? (
          <div className="py-14 text-center border border-dashed border-[#E5E2D9] rounded-md bg-white">
            <MusicNote size={32} className="inline-block text-[#8A8780] mb-2" weight="duotone"/>
            <div className="text-sm text-[#5C5A55]">No sounds in this category — upload one above.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {rowsForTab.map(row => (
              <SoundRow key={row.id} row={row}
                        playing={playingId === row.id}
                        onPlay={() => play(row)}
                        onStop={stopAudio}
                        onEdit={() => setEditing(row)}
                        onDelete={() => remove(row)}
                        onSetDefault={() => setDefault(row)}
                        onToggleActive={() => toggleActive(row)}/>
            ))}
          </div>
        )}
      </div>

      {showUpload && (
        <UploadModal
          initialCategory={tab}
          onClose={() => setShowUpload(false)}
          onSaved={() => { setShowUpload(false); load(); }}
        />
      )}
      {editing && (
        <EditModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </AppShell>
  );
}

// ─── Row ───────────────────────────────────────────────
const SoundRow = ({ row, playing, onPlay, onStop, onEdit, onDelete, onSetDefault, onToggleActive }) => (
  <div className={`flex items-center gap-3 rounded-xl bg-white border border-[#E5E2D9] px-4 py-3 ${row.active ? "" : "opacity-60"}`}
       data-testid={`alarm-row-${row.id}`}>
    <button onClick={playing ? onStop : onPlay}
      data-testid={`alarm-play-${row.id}`}
      className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 transition-colors ${
        playing ? "bg-[#DC2626] text-white" : "bg-[#FEE2E2] text-[#DC2626] hover:bg-[#FDCACA]"
      }`}>
      {playing ? <Pause size={18} weight="fill"/> : <Play size={18} weight="fill"/>}
    </button>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-bold text-[#111827] flex items-center gap-2">
        {row.name}
        {row.is_default && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded bg-[#FFF8E6] text-[#7A5A11] font-bold">
            <Star size={9} weight="fill"/> default
          </span>
        )}
      </div>
      <div className="text-[11px] text-[#5C5A55] mt-0.5 flex gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1"><SpeakerHigh size={10} weight="bold"/> {row.language ? row.language.toUpperCase() : "—"}</span>
        {row.gender_hint && <span className="text-[#8A8780]">· {row.gender_hint}</span>}
        {row.duration_sec ? <span className="text-[#8A8780]">· {row.duration_sec.toFixed(1)}s</span> : null}
      </div>
    </div>
    {!row.is_default && (
      <button onClick={onSetDefault} data-testid={`alarm-default-${row.id}`}
        className="h-8 px-2 border border-[#E5E2D9] rounded-md text-[11px] font-semibold hover:bg-[#F7F6F2] inline-flex items-center gap-1"
        title="Mark as default for this category">
        <Star size={11} weight="bold"/>
      </button>
    )}
    <button onClick={onToggleActive} data-testid={`alarm-toggle-${row.id}`}
      className={`h-8 px-2 rounded-md text-[10px] font-bold uppercase tracking-[0.14em] ${
        row.active ? "bg-[#EEF7EC] text-[#166534]" : "bg-[#F4C7B9] text-[#7A2E18]"
      }`}>
      {row.active ? "Active" : "Off"}
    </button>
    <button onClick={onEdit} data-testid={`alarm-edit-${row.id}`}
      className="w-8 h-8 rounded-md border border-[#E5E2D9] hover:bg-[#F7F6F2] inline-flex items-center justify-center">
      <PencilSimple size={12} weight="bold" className="text-[#5C5A55]"/>
    </button>
    <button onClick={onDelete} data-testid={`alarm-delete-${row.id}`}
      className="w-8 h-8 rounded-md border border-[#F4C7B9] text-[#DC2626] hover:bg-[#FEE2E2] inline-flex items-center justify-center">
      <Trash size={12} weight="bold"/>
    </button>
  </div>
);

// ─── Upload modal ──────────────────────────────────────
const UploadModal = ({ initialCategory, onClose, onSaved }) => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState(initialCategory || "medicine");
  const [language, setLanguage] = useState("");
  const [gender, setGender] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onPick = (f) => {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) return toast.error("File exceeds 25 MB");
    setFile(f);
    if (!name) setName(f.name.replace(/\.[^/.]+$/, ""));
  };
  const submit = async () => {
    if (!file) return toast.error("Choose an audio file");
    if (!name.trim()) return toast.error("Name is required");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = JSON.parse(localStorage.getItem("rescigo:auth") || "{}").token;
      const up = await fetch(`${API_BASE}/admin/alarm-sounds/upload`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!up.ok) throw new Error(`Upload failed (${up.status})`);
      const j = await up.json();
      await api.post("/crm/alarm-sounds", {
        name: name.trim(), url: j.url, category,
        language: language || null, gender_hint: gender || null,
        is_default: isDefault, active: true,
      });
      toast.success("Uploaded");
      onSaved();
    } catch (e) { toast.error(e.message || formatApiError(e)); }
    finally { setUploading(false); }
  };

  return (
    <ModalShell title="Upload alarm audio" onClose={onClose}>
      <div className="space-y-3">
        <label className="block rounded-xl border-2 border-dashed border-[#E5E2D9] p-6 text-center cursor-pointer hover:border-[#DC2626] hover:bg-[#FEF7F6] transition"
               data-testid="alarms-file-drop">
          <UploadSimple size={26} weight="duotone" className="inline-block text-[#DC2626]"/>
          <div className="text-sm font-bold text-[#111827] mt-2">
            {file ? file.name : "Click to choose audio file"}
          </div>
          <div className="text-[11px] text-[#8A8780] mt-1">
            WAV · MP3 · OGG · M4A · AAC · FLAC (max 25 MB)
          </div>
          <input type="file" accept="audio/*" className="hidden"
                 onChange={(e) => onPick(e.target.files?.[0])}
                 data-testid="alarms-file-input"/>
        </label>

        <Field label="Display name">
          <input value={name} onChange={e => setName(e.target.value)}
                 data-testid="alarms-name"
                 className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
        </Field>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Category">
            <select value={category} onChange={e => setCategory(e.target.value)}
                    data-testid="alarms-category"
                    className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Language">
            <select value={language} onChange={e => setLanguage(e.target.value)}
                    className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
              {LANGS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </Field>
          <Field label="Voice">
            <select value={gender} onChange={e => setGender(e.target.value)}
                    className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
              {GENDERS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)}
                 data-testid="alarms-default"/>
          Set as default for {CATEGORIES.find(c => c.key === category)?.label}
        </label>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
        <button onClick={submit} disabled={uploading} data-testid="alarms-submit"
          className="h-10 px-5 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-semibold disabled:opacity-50">
          {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>
    </ModalShell>
  );
};

// ─── Edit modal (rename / category / language / voice) ───
const EditModal = ({ row, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: row.name, category: row.category,
    language: row.language || "", gender_hint: row.gender_hint || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/crm/alarm-sounds/${row.id}`, form);
      toast.success("Updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Edit alarm sound" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Name">
          <input value={form.name} onChange={e => setForm(s => ({ ...s, name: e.target.value }))}
                 className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
        </Field>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Category">
            <select value={form.category} onChange={e => setForm(s => ({ ...s, category: e.target.value }))}
                    className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Language">
            <select value={form.language} onChange={e => setForm(s => ({ ...s, language: e.target.value }))}
                    className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
              {LANGS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </Field>
          <Field label="Voice">
            <select value={form.gender_hint} onChange={e => setForm(s => ({ ...s, gender_hint: e.target.value }))}
                    className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
              {GENDERS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </Field>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
        <button onClick={save} disabled={busy}
          className="h-10 px-5 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-semibold disabled:opacity-50">
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </ModalShell>
  );
};

const ModalShell = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
    <div onClick={e => e.stopPropagation()}
         className="bg-white rounded-2xl w-full max-w-lg my-8 shadow-2xl overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-[#E5E2D9] bg-[#FAFAF7]">
        <MusicNote size={18} weight="fill" className="text-[#DC2626]"/>
        <div className="font-heading text-lg flex-1">{title}</div>
        <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white inline-flex items-center justify-center">
          <X size={16} weight="bold"/>
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold mb-1">{label}</div>
    {children}
  </label>
);
